import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import localforage from 'localforage'

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  organization: string
  organization_slug: string
  role: {
    id: number
    name: string
    name_en: string
    geographic_level: number | null
  } | null
  permissions: string[]
  is_authenticated: boolean
  is_superuser: boolean
}

const authStorage = localforage.createInstance({
  name: 'EstipanaDB',
  storeName: 'auth',
  description: 'Authentication storage for Estipana'
})

const fetchAuthStatus = async (): Promise<User> => {
  try {
    const response = await fetch('/api/core/me/')
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    const data = await response.json()
    // Cache auth state
    await authStorage.setItem('auth_user', data)
    
    // Cache CSRF token from cookie if available
    const csrfToken = document.cookie.split('estipana_csrftoken=')[1]?.split(';')[0]
    if (csrfToken) {
      await authStorage.setItem('csrftoken', csrfToken)
    }
    
    return data
  } catch (error) {
    // If offline / network error, attempt to load cached auth state
    const cached = await authStorage.getItem<User>('auth_user')
    if (cached) {
      return cached
    }
    throw error
  }
}

export const useAuth = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: user, isLoading, isError } = useQuery<User>({
    queryKey: ['auth-user'],
    queryFn: fetchAuthStatus,
    retry: false,
  })

  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Clear cache on logout
      await authStorage.removeItem('auth_user')
      await authStorage.removeItem('csrftoken')

      try {
        const response = await fetch('/api/core/logout/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('estipana_csrftoken') || '',
          },
        })
        if (!response.ok) {
          throw new Error('Logout failed')
        }
        return response.json()
      } catch (err) {
        // Even if network call fails, we proceed since caches are cleared
        return { success: true }
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(['auth-user'], null)
      navigate('/')
    },
  })

  return {
    user,
    isLoading,
    isError,
    isAuthenticated: !!user?.is_authenticated,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  }
}

function getCookie(name: string) {
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}
